import type { ComponentProps } from 'react';

import { forwardRef, useEffect, useMemo, useRef } from 'react';

import useClassy from '@core/hooks/useClassy';
import useElementResizeObserver from '@core/hooks/useElementResizeObserver';
import Input from '@ui/Input';
import Tooltip from '@ui/Tooltip';

import styles from './index.module.scss';

interface Props extends ComponentProps<typeof Input> {
  baseVersion: string;
}

const VERSION_PREFIX_MAX_DISPLAY_LENGTH = 10;

/**
 * A wrapper around `@ui/Input` that provides default behavior and styling for
 * entering a branch name value. For example, providing the parent version
 * prefix "v2.0_", truncating long version prefixes, and disabling auto complete.
 */
const BranchNameInput = forwardRef<HTMLInputElement, Props>(function BranchNameInput(
  { baseVersion, className, ...rest }: Props,
  ref,
) {
  const bem = useClassy(styles, 'BranchNameInput');

  const containerRef = useRef<HTMLDivElement>(null);
  const prefixRef = useRef<HTMLSpanElement>(null);
  const { width: prefixWidth = 0 } = useElementResizeObserver(prefixRef) || {};

  /**
   * We have to truncate in JS rather than CSS, because our font is not
   * monospaced. When using CSS with either a `ch` or `px` based `max-width`,
   * there can be extra whitespace after the ellipsis with certain character
   * combinations.
   */
  const truncatedBaseVersion = useMemo(() => {
    if (baseVersion.length > VERSION_PREFIX_MAX_DISPLAY_LENGTH) {
      return `${baseVersion.slice(0, VERSION_PREFIX_MAX_DISPLAY_LENGTH)}...`;
    }
    return baseVersion;
  }, [baseVersion]);

  /**
   * Dynamically set the width of the prefix element, since we can't know how
   * long the base version name will be until we render and truncate it.
   */
  useEffect(() => {
    const inputParent = containerRef.current?.querySelector<HTMLDivElement>('.InputParent');
    if (inputParent) {
      // We need to set this variable on the `.InputParent` element so that it
      // takes precedence over the value set in that component's stylesheet.
      // Setting the variable any higher in the DOM tree will cause it to be
      // lower in precedence than the stylesheet value.
      inputParent.style.setProperty('--Input-prefix-width', `${prefixWidth}px`);
    }
  }, [prefixWidth]);

  return (
    <div ref={containerRef}>
      <Input
        ref={ref}
        autoComplete="off"
        className={bem('&', className)}
        placeholder="add-ai-docs"
        prefix={
          <span ref={prefixRef} className={bem('-prefix')}>
            {/**
             * truncate very long parent version names, but always display
             * the _ separator to reinforce our branch naming convention
             */}
            <Tooltip asTitle content={baseVersion} disabled={baseVersion === truncatedBaseVersion}>
              <span>v{truncatedBaseVersion}</span>
            </Tooltip>
            _
          </span>
        }
        size="sm"
        {...rest}
      />
    </div>
  );
});

export default BranchNameInput;
